/**
 * Pure derivation of the composer's run-control state (stop/send button + input
 * lock). Extracted from the panel so the startup-vs-stuck lifecycle can be unit
 * tested without rendering the whole component (ENG-2748).
 *
 * Background: a run is started over the websocket, and the FE only learns its
 * runId once the `run_metadata` event arrives. Between dispatching the send and
 * receiving that event, the state is (isWaitingForResponse: true, activeRunId:
 * null) — the SAME state a run gets stuck in when its completion event is lost.
 * The two are indistinguishable by state alone; they differ only by elapsed
 * time. `stuckRecoveryEligible` is the caller's time-based signal that the
 * startup grace period has passed, so a still-missing runId now means a
 * lost/failed run rather than a normal startup, and local recovery is safe.
 */

export const STUCK_RUN_RECOVERY_DELAY_MS = 10_000;

export interface ComposerRunControlsInput {
  activeRunId: string | null;
  isLoading: boolean;
  isWaitingForResponse: boolean;
  isCancellingRun: boolean;
  hasPendingInterrupt: boolean;
  isLegacySession: boolean;
  checkpointSwitchLoading: boolean;
  hasSendableContent: boolean;
  /**
   * True once the startup grace period has elapsed while still waiting with no
   * runId — i.e. the run appears genuinely stuck, not merely starting up.
   */
  stuckRecoveryEligible: boolean;
}

export interface ComposerRunControls {
  isRunActive: boolean;
  /** The composer is waiting for a run to start but has no runId yet. */
  awaitingRunStart: boolean;
  showCancelButton: boolean;
  sendButtonDisabled: boolean;
}

export function deriveComposerRunControls(input: ComposerRunControlsInput): ComposerRunControls {
  const {
    activeRunId,
    isLoading,
    isWaitingForResponse,
    isCancellingRun,
    hasPendingInterrupt,
    isLegacySession,
    checkpointSwitchLoading,
    hasSendableContent,
    stuckRecoveryEligible,
  } = input;

  const isRunActive = activeRunId !== null || isLoading || isWaitingForResponse;

  const awaitingRunStart =
    activeRunId === null && (isWaitingForResponse || isLoading) && !hasPendingInterrupt;

  // Cancel is offered either when there is a real run to stop (activeRunId set,
  // cancelled over REST) or when a run is stuck: we have been awaiting a run
  // start past the grace period, so no run_metadata is coming and the composer
  // would otherwise freeze forever (handleCancelRun then recovers locally).
  //
  // Crucially, during the normal startup window (awaitingRunStart but grace
  // period not yet elapsed) the cancel button is NOT shown, so a genuinely
  // starting run can never be abandoned — which would otherwise let a second
  // message submit while the first run is still active on the backend.
  const canRecoverStuckRun = awaitingRunStart && stuckRecoveryEligible;
  const showCancelButton = (activeRunId !== null || canRecoverStuckRun) && !hasPendingInterrupt;

  const sendButtonDisabled =
    isLegacySession ||
    checkpointSwitchLoading ||
    hasPendingInterrupt ||
    (!showCancelButton && !hasSendableContent) ||
    (showCancelButton ? isCancellingRun : isRunActive);

  return { isRunActive, awaitingRunStart, showCancelButton, sendButtonDisabled };
}
